<script setup lang="ts">
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchResult from './SearchResult.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import ModalNuevo from '@/components/Modals/ModalNuevo/ModalNuevo.vue'
import SearchInput from './SearchInput.vue'
// import { useGa } from '@/composables/useGa'
import type { SearchedEstablishment } from '@/types/SearchedEstablishment'
import { useHttp } from "@/composables/useHttp"
import { useEstablishmentHttp } from "@/http/establishmentHttp"
import { useMarketplaceStore } from "@/stores/ssr/useMarketplaceStore"
import { useSessionStore } from "@/stores/useSessionStore"

const { t } = useI18n()
// const ga = useGa()

defineProps<{ open: boolean }>()

const emit = defineEmits([ 'close' ])
const http = useHttp()

const state = reactive<{
  establishments: SearchedEstablishment[]
  count: number | null
  query: string
}>({
  establishments: [],
  count: null,
  query: '',
})

const sessionStore = useSessionStore()

const buttonText = computed(() => {
  if (!state.query) {
    return t('marketplace_search.type_to_search')
  }

  return t('marketplace_search.show_amount_of_results', { amountOfResults: state.count })
})

const marketplaceStore = useMarketplaceStore()

const showResults = async () => {
  marketplaceStore.setSearchQuery(state.query)

  close()
}

const { searchEstablishments } = useEstablishmentHttp()

const gatherPredictions = async (query: string | null) => {
  state.establishments = []
  state.count = null

  if (query === null || query.length < 2) {
    return
  }

  if (!sessionStore.location?.coordinates?.lat || !sessionStore.location?.coordinates?.lng || !sessionStore.distribution) {
    throw new Error('Could not search due to missing parameters')
  }

  state.query = query

  const response = await searchEstablishments({
    q: query,
    distribution: sessionStore.distribution,
    location: {
      lat: sessionStore.location.coordinates.lat,
      lng: sessionStore.location.coordinates.lng,
    },
    sort: marketplaceStore.sortingKey,
  })

  state.count = response.count
  state.establishments = response.establishments

  // ga.trackSearch({ query, count: response.count })
}

function close() {
  state.establishments = []
  state.count = null
  state.query = ''

  emit('close')
}
</script>

<template>
  <ModalNuevo class="search-modal"
              :open="open"
              data-test-id="search-modal"
              @close="close">
    <template #modal-header>
      <div class="search-modal__header">
        <SearchInput :placeholder="t('search.placeholder')" @search="gatherPredictions"/>
      </div>
    </template>

    <div class="search-modal__content" data-test-id="search-results">
      <template v-if="state.count > 0 && state.query">
        <div class="search-modal__title">
          {{ t('marketplace_search.good_matches') }}
        </div>
        <SearchResult v-for="establishment in state.establishments"
                      :key="establishment.id"
                      :establishment="establishment"/>
      </template>

      <div class="search-modal__text">
        <template v-if="state.count === 0">
          {{ t('marketplace_search.no_results_found_try_again') }}
        </template>
      </div>
    </div>

    <template v-if="state.count > 0 && state.query" #modal-bottom>
      <div class="search-modal__bottom">
        <Button data-test-id="show-all-results-button" @click="showResults">
          {{ buttonText }}
        </Button>
      </div>
    </template>
  </ModalNuevo>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/styling.scss";

.search-modal {
  &__header {
    padding: 2rem 2rem 1rem;
  }

  &__title {
    @include gradient-text;
    max-width:   max-content;
    font-weight: 700;
    font-size:   1.25rem;
    margin-bottom: 1.75rem;
  }

  &__text {
    text-align: center;
    width: 100%;
  }

  &__header,
  &__bottom {
    display: flex;
    justify-content: center;
    width:   100%;
  }
}
</style>
